import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { feedbackService } from 'src/dataServices/FeedbackService';
import localeService from 'src/dataServices/LocaleService';
import { IApplicationState } from 'src/state';
import { useTicketFormContext } from '../TicketFormActiveContext';
import { TicketForm } from './TicketForm';
import { TicketProposal } from './TicketProposal';
import './TicketProposal.scss';
import { TicketStatusCard } from './TicketStatusCard';
import { ITicketDraft, TicketCompletionResult } from './types';

interface TicketProposalFlowProps {
  data: ITicketDraft;
  respond?: (result: TicketCompletionResult) => void;
}

type FlowStep = 'proposal' | 'form' | 'error';

export function TicketProposalFlow({ data, respond }: TicketProposalFlowProps) {
  const [step, setStep] = useState<FlowStep>(data.ticketForm ? 'form' : 'proposal');
  const { setTicketFormActive } = useTicketFormContext();
  const { supportTicketResponse, supportTicketEmail } = useSelector((state: IApplicationState) => state.contactUs);
  const hasResponded = useRef(false);
  const respondReady = typeof respond === 'function';

  // Lock the chat input for the entire lifetime of the flow so the user cannot send
  // a new chat message while the human-in-the-loop propose_ticket action is still
  // pending a response (doing so produces an HTTP 400 because the assistant
  // tool_call has no matching tool result).
  useEffect(() => {
    setTicketFormActive(true);
    return () => setTicketFormActive(false);
  }, [setTicketFormActive]);

  const complete = useCallback(
    (result: TicketCompletionResult) => {
      if (hasResponded.current || !respond) {
        return;
      }

      hasResponded.current = true;
      if (result.result === 'cancelled') {
        void feedbackService.logSupportTicketEvent({
          threadId: data.agentMetadata.threadId,
          outcome: 'Cancelled'
        });
      }

      respond(result);
    },
    [data.agentMetadata.threadId, respond]
  );

  if (step === 'proposal') {
    return (
      <TicketProposal
        respondReady={respondReady}
        onYes={() => setStep('form')}
        onNo={() =>
          complete({
            result: 'cancelled',
            message: 'User declined to create a ticket. Continue helping them with their questions.'
          })
        }
      />
    );
  }

  if (step === 'form') {
    return (
      <TicketForm
        data={data}
        actionsEnabled={respondReady}
        onSubmit={() =>
          complete({
            result: 'submitted',
            ticketId: String(supportTicketResponse?.Id ?? ''),
            email: supportTicketEmail ?? '',
            message: 'User submitted a support ticket successfully.'
          })
        }
        onCancel={() =>
          complete({
            result: 'cancelled',
            message: 'User cancelled the ticket form. Continue helping them with their questions.'
          })
        }
        onError={() => setStep('error')}
      />
    );
  }

  return (
    <TicketStatusCard
      variant="error"
      title={localeService.getLocaleContent().ticketErrorTitle}
      copy={localeService.getLocaleContent().ticketErrorMessage}
      actions={
        <>
          <button onClick={() => setStep('form')} className="support-ticket-button support-ticket-button--danger">
            {localeService.getLocaleContent().ticketErrorTryAgainButton}
          </button>
          <button
            onClick={() =>
              complete({
                result: 'cancelled',
                message: 'User cancelled the ticket flow after an error. Continue helping them with their questions.'
              })
            }
            className="support-ticket-button support-ticket-button--secondary"
          >
            {localeService.getLocaleContent().ticketErrorCancelButton}
          </button>
        </>
      }
    />
  );
}

